import React from "react";
import { cn } from "@/lib/utils";

export type SegmentedOption<T extends string> = {
  value: T;
  label: React.ReactNode;
  icon?: React.ReactNode;
  /** Accessible name when `label` is icon-only. */
  ariaLabel?: string;
};

type Props<T extends string> = {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: "sm" | "md";
  /** Names the group for screen readers (e.g. "Table view"). */
  ariaLabel?: string;
  className?: string;
};

/** Segmented control — a pill-group toggle where the selected segment lifts to a
 *  raised white chip. 2–4 segments, sm/md. One canonical control superseding the
 *  hand-rolled `.src-view` / `.wb-view` / `.vb-seg`. Each button carries
 *  `aria-pressed`; wrap in a labelled group. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  size = "sm",
  ariaLabel,
  className,
}: Props<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn("vb-segmented", size === "sm" && "vb-segmented--sm", className)}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            className="vb-segmented__btn"
            aria-pressed={on}
            aria-label={o.ariaLabel}
            onClick={() => onChange(o.value)}
          >
            {o.icon != null && (
              <span aria-hidden="true" className="inline-flex items-center">
                {o.icon}
              </span>
            )}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default Segmented;
